<template>
  <nuxt-layout name="default" v-if="profileData">
    <div v-if="isProfileVisible">
      <div v-if="isOwner && !isPreview" class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-2">
          <span
            class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
            :class="ownerIsVisible
              ? 'bg-green-500/10 text-green-400'
              : 'bg-gray-500/10 text-gray-400'"
          >
            <Icon
              :name="ownerIsVisible ? 'solar:eye-bold-duotone' : 'solar:eye-closed-bold-duotone'"
              size="0.875rem"
            />
            {{ ownerIsVisible ? $t('profilePreview.statusPublic') : $t('profilePreview.statusPrivate') }}
          </span>
          <span class="text-xs text-gray-400">
            {{ ownerIsVisible ? $t('profilePreview.statusPublicHint') : $t('profilePreview.statusPrivateHint') }}
          </span>
        </div>
        <b-button variant="primary" light size="sm" @click="toggle" data-e2e="public-profile-preview-button">
          <Icon name="solar:eye-bold-duotone" size="1rem" class="mr-2" />
          {{ $t('profilePreview.viewAsPublic') }}
        </b-button>
      </div>
      <div class="grid grid-cols-12 gap-8" data-e2e="public-profile-grid">
        <div class="col-span-12 lg:col-span-3 flex flex-col gap-8">
          <b-profile-information-card />
          <b-profile-contact-card />
          <b-profile-client-cases-card />
          <b-profile-audio-demo-card />
          <b-profile-about-card />
        </div>
        <div class="col-span-12 lg:col-span-9 flex flex-col gap-8">
          <ClientOnly>
            <b-profile-matching-card
              v-if="matchingData?.score_breakdown"
              :match-score="matchingData.match_score"
              :score-breakdown="matchingData.score_breakdown"
            />
          </ClientOnly>
          <ClientOnly>
            <b-profile-big-score-card
              :slug="slug"
              :is-owner="!!isOwner && !isPreview"
              :user-name="profileData?.user ? `${profileData.user.first_name} ${profileData.user.last_name}` : ''"
              :title="profileData?.user?.profile?.title ?? null"
              :photo-url="profileData?.user?.profile?.signed_picture_url ?? null"
            />
          </ClientOnly>
          <b-profile-skills-card :user="storeUser" />
          <b-profile-experiences-card :user="storeUser" class="mb-10" />
        </div>
      </div>
    </div>
    <div v-else class="text-white text-center py-10" data-e2e="public-profile-error">
      {{ errorMessage }}
    </div>

    <!-- Preview mode exit banner (only shown when preview is active) -->
    <ClientOnly>
      <div v-if="isOwner && isPreview" class="fixed bottom-0 left-0 right-0 z-50" data-e2e="public-profile-preview-banner">
        <div class="flex items-center justify-center gap-4 px-6 py-3 text-sm bg-white/10 text-white backdrop-blur-sm">
          <Icon name="solar:eye-bold-duotone" size="1.25rem" class="text-gray-300" />
          <span>{{ $t('profilePreview.active') }}</span>
          <b-button variant="primary" light size="xs" @click="toggle" data-e2e="public-profile-exit-preview-button">
            {{ $t('profilePreview.exitPreview') }}
          </b-button>
        </div>
      </div>
    </ClientOnly>
  </nuxt-layout>
</template>

<script setup lang="ts">
import BProfileInformationCard from "~/components/card/BProfileInformationCard.vue";
import BProfileExperiencesCard from "~/components/card/BProfileExperiencesCard.vue";
import BProfileSkillsCard from "~/components/card/BProfileSkillsCard.vue";
import BProfileAboutCard from "~/components/card/BProfileAboutCard.vue";
import BProfileClientCasesCard from "~/components/card/BProfileClientCasesCard.vue";
import BProfileAudioDemoCard from "~/components/card/BProfileAudioDemoCard.vue";

import BProfileContactCard from "~/components/card/BProfileContactCard.vue";
import BProfileBigScoreCard from "~/components/card/BProfileBigScoreCard.vue";
import BProfileMatchingCard from "~/components/card/BProfileMatchingCard.vue";
import BButton from "~/components/forms/BButton.vue";
import type { UserMatchingResponse } from "~/api/v2/experts/ExpertRepository";

definePageMeta({
  auth: false,
  company: false,
});

const route = useRoute();
const config = useRuntimeConfig();
const slug = computed(() => route.params.slug as string);


// SSR-compatible data fetching
const { data: profileData, error: profileError } = await useAsyncData(
  `profile-${slug.value}`,
  async () => {
    const $api = useNuxtApp().$api();
    const user = await $api.v2.users.get(slug.value);
    const { is_visible } = await $api.v2.users.getVisibility(slug.value);
    return { user, is_visible };
  },
  {
    watch: [slug],
  }
);

const experienceCreationIsOpen = ref(false);
provide('experienceCreationIsOpen', experienceCreationIsOpen);

const auth = authStore();
const { user: loggedInUser } = storeToRefs(auth);

const { isPreview, toggle, reset: resetPreview } = useProfilePreviewMode();

const isOwner = computed(() => loggedInUser.value && profileData.value && loggedInUser.value.id === profileData.value.user.id);
const ownerIsVisible = computed(() => loggedInUser.value?.profile?.is_visible ?? profileData.value?.is_visible ?? false);

const isProfileVisible = computed(() => {
  if (!profileData.value) return false;
  if (isOwner.value && !isPreview.value) {
    return true;
  }
  return profileData.value.is_visible;
});

const errorMessage = ref("Ce profil est privé ou n'existe pas.");

// Reset preview on slug change (same-route navigation reuses the component — onUnmounted won't fire)
watch(slug, () => resetPreview());
onUnmounted(() => resetPreview());

// Handle 404 errors for SSR
if (profileError.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Profil non trouvé',
  });
}

// Return 404 for private profiles (prevents soft 404 SEO issue)
if (import.meta.server && profileData.value && !profileData.value.is_visible) {
  const event = useRequestEvent();
  if (event) {
    setResponseStatus(event, 404, 'Profile Not Found');
  }
}

// SEO Meta tags (rendered on server)
const ogImageUrl = 'https://weplaybig.com/hubfs/Slide%2016_9%20-%207.png';

useSeoMeta({
  title: () => profileData.value?.user
    ? `${profileData.value.user.first_name} ${profileData.value.user.last_name} - BIG Platform`
    : 'Profil Freelance - BIG Platform',
  description: () => profileData.value?.user?.profile?.summary
    || `Découvrez le profil professionnel de ${profileData.value?.user?.first_name || 'ce freelance'} sur BIG Platform`,
  ogTitle: () => profileData.value?.user
    ? `${profileData.value.user.first_name} ${profileData.value.user.last_name} - Freelance Sales`
    : 'Profil Freelance',
  ogDescription: () => profileData.value?.user?.profile?.summary
    || 'Profil professionnel de freelance commercial',
  ogImage: ogImageUrl,
  ogImageWidth: 3072,
  ogImageHeight: 1728,
  ogUrl: () => `${config.public.NUXT_APP_URL}account/${route.params.slug}`,
  ogType: 'profile',
  twitterCard: 'summary_large_image',
  twitterImage: ogImageUrl,
});

const userStore = useUserStore();
const storeUser = computed(() => userStore.user);

watch(profileData, (newData) => {
  if (newData?.user) {
    userStore.user = newData.user;
  }
}, { immediate: true });

// Mission matching context (from query param)
const missionId = computed(() => route.query.mission as string | undefined);
const matchingData = ref<UserMatchingResponse | null>(null);

// Advanced-mode weights forwarded from the expert search page. When present, the
// profile score must reflect the same weights as the list score.
const WEIGHT_KEYS = [
  'deal_size_weight',
  'market_segment_weight',
  'persona_weight',
  'industry_weight',
  'company_size_weight',
] as const;

const matchingWeights = computed(() => {
  const weights: Record<string, number> = {};
  for (const key of WEIGHT_KEYS) {
    const raw = route.query[key];
    const value = Number(Array.isArray(raw) ? raw[0] : raw);
    if (raw !== undefined && raw !== null && raw !== '' && !Number.isNaN(value)) {
      weights[key] = value;
    }
  }
  return Object.keys(weights).length > 0 ? weights : undefined;
});

// Fetch reference data only on client (not needed for SEO)
if (import.meta.client) {
  const dealSizeStore = useDealSizeStore();
  const companySizeStore = useCompanySizeStore();
  const personaStore = usePersonaStore();
  const marketSegments = useMarketSegmentStore();
  const industryStore = useIndustryStore();

  const { shouldStart: shouldStartOnboardingTour, start: startOnboardingTour } = useFreelanceOnboardingTour("account");

  onMounted(() => {
    dealSizeStore.fetchDealSizes();
    companySizeStore.fetchCompanySizes();
    personaStore.fetchPersonas();
    marketSegments.fetchMarketSegments();
    industryStore.fetchIndustries();

    if (isOwner.value && shouldStartOnboardingTour()) {
      startOnboardingTour();
    }
  });

  // Track expert profile views (LIR1 "profil consulté"); excludes the owner viewing their own profile
  const { trackEvent } = useTracking();
  watch(slug, () => {
    if (!isOwner.value) trackEvent("profile", "expert_profile_viewed");
  }, { immediate: true });

  // Fetch matching data when navigating from a mission context
  watch(
    [() => profileData.value?.user?.id, missionId, matchingWeights],
    async ([userId, mId, weights]) => {
      if (!userId || !mId) {
        matchingData.value = null;
        return;
      }
      try {
        const $api = useNuxtApp().$api();
        matchingData.value = await $api.v2.experts.getUserMatching(mId, userId, weights);
      } catch {
        matchingData.value = null;
      }
    },
    { immediate: true },
  );
}
</script>
